import { Controller } from 'react-hook-form'

import { Upload, FileSignature } from 'lucide-react'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { IntegratedFileSelect } from '@/components/ui/IntegratedFileSelect'
import companyFormStyles from '../../styles/organizationCard.module.css'
import type { OrganizationEditSectionProps } from '../../types/components'
import { OrganizationDefaultFormDownloadLink } from './OrganizationDefaultFormDownloadLink'
import {
  ORGANIZATION_STATIC_ACH_FORM_PATH,
  ORGANIZATION_STATIC_STATEMENT_INSTRUCTION_PATH,
} from '../../utils/downloadOrganizationLogo'
import {
  DEFAULT_PAYMENT_INFO_VARIABLES,
  DEFAULT_STATEMENT_FORM_VARIABLES,
  ORG_FORM_SUBHEADING,
} from '../../constants/editPage'

export function OrganizationDefaultFormTab({ ctx }: OrganizationEditSectionProps) {
  return (
        <div
          id="org-panel-defaultform"
          role="tabpanel"
          aria-labelledby="org-tab-defaultform"
          hidden={ctx.selectedTab !== 'defaultform'}
        >
        <div className="space-y-6">
          {/* Form Upload Card */}
          <div className={companyFormStyles.card}>
            <div className={companyFormStyles.cardHeader}>
              <Upload className={companyFormStyles.cardIcon} />
              <h2 className={companyFormStyles.cardTitle}>
                Form Upload
              </h2>
            </div>
            <div className={companyFormStyles.cardBody}>
              <div className="grid min-w-0 grid-cols-1 gap-6 sm:grid-cols-2">
                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>ACH Form</label>
                  <IntegratedFileSelect
                    displayName={ctx.displayDefaultFormFileLine('ach_form_file')}
                    accept={ctx.defaultFormPdfAccept}
                    emptyLabel="No file selected"
                    browseLabel="Select"
                    disabled={ctx.submitting}
                    onChange={(e) =>
                      ctx.onDefaultFormFilePick('ach_form_file', e.target.files?.[0] ?? null)
                    }
                    data-testid="org-ach-form-upload"
                  />
                  {ctx.isEdit && ctx.orgId != null && (
                    <OrganizationDefaultFormDownloadLink
                      label="Download ACH Form"
                      disabled={ctx.submitting}
                      busy={ctx.defaultFormDownloading === 'ach_form'}
                      onClick={() =>
                        void ctx.runDefaultFormDownload(
                          'ach_form',
                          'ACH_FORM',
                          ORGANIZATION_STATIC_ACH_FORM_PATH,
                        )
                      }
                    />
                  )}
                </div>

                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel}>
                    Statement Instruction File
                  </label>
                  <IntegratedFileSelect
                    displayName={ctx.displayDefaultFormFileLine('statement_instruction_file')}
                    accept={ctx.defaultFormPdfAccept}
                    emptyLabel="No file selected"
                    browseLabel="Select"
                    disabled={ctx.submitting}
                    onChange={(e) =>
                      ctx.onDefaultFormFilePick(
                        'statement_instruction_file',
                        e.target.files?.[0] ?? null,
                      )
                    }
                    data-testid="org-statement-instruction-upload"
                  />
                  {ctx.isEdit && ctx.orgId != null && (
                    <OrganizationDefaultFormDownloadLink
                      label="Download Statement Instruction File"
                      disabled={ctx.submitting}
                      busy={ctx.defaultFormDownloading === 'statement_instruction'}
                      onClick={() =>
                        void ctx.runDefaultFormDownload(
                          'statement_instruction',
                          'STATEMENT_INSTRUCTION',
                          ORGANIZATION_STATIC_STATEMENT_INSTRUCTION_PATH,
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Default Payment Statement Form Card */}
          <div className={companyFormStyles.card}>
            <div className={companyFormStyles.cardHeader}>
              <FileSignature className={companyFormStyles.cardIcon} />
              <h2 className={companyFormStyles.cardTitle}>
                Default Payment Statement Form
              </h2>
            </div>
            <div className={companyFormStyles.cardBody}>
              <div className="space-y-5">
                <div>
                  <p className={ORG_FORM_SUBHEADING} id="org-default-stmt-vars-heading">
                    Available Variables For Statement:
                  </p>
                  <div
                    className={companyFormStyles.variablesBox}
                    role="list"
                    aria-labelledby="org-default-stmt-vars-heading"
                    aria-label="Default payment statement template variables"
                  >
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                      {DEFAULT_STATEMENT_FORM_VARIABLES.map((v) => (
                        <code
                          key={v}
                          className={companyFormStyles.variableCode}
                        >
                          {v}
                        </code>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <p className={ORG_FORM_SUBHEADING} id="org-default-payment-info-vars-heading">
                    Available Variables for Payment Information:
                  </p>
                  <div
                    className={companyFormStyles.variablesBox}
                    role="list"
                    aria-labelledby="org-default-payment-info-vars-heading"
                    aria-label="Payment information block variables"
                  >
                    <div className="flex flex-wrap gap-x-2 gap-y-2">
                      {DEFAULT_PAYMENT_INFO_VARIABLES.map((v) => (
                        <code
                          key={v}
                          className={companyFormStyles.variableCode}
                        >
                          {v}
                        </code>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={companyFormStyles.formGroup}>
                  <label
                    className={companyFormStyles.formLabel}
                    htmlFor="org-default-payment-statement"
                  >
                    Default Payment Statement Form Template
                  </label>
                  <Controller
                    name="default_payment_statement_form"
                    control={ctx.control}
                    render={({ field: { value, onChange } }) => (
                      <CKEditor4
                        id="org-default-payment-statement"
                        value={value || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>

                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel} htmlFor="org-lost-revenue-default">
                    Lost revenue content
                  </label>
                  <Controller
                    name="lost_revenue_content"
                    control={ctx.control}
                    render={({ field: { value, onChange } }) => (
                      <CKEditor4 id="org-lost-revenue-default" value={value || ''} onChange={onChange} />
                    )}
                  />
                </div>

                <div className={companyFormStyles.formGroup}>
                  <label className={companyFormStyles.formLabel} htmlFor="org-payment-info-default">
                    Payment information row
                  </label>
                  <Controller
                    name="payment_information_row"
                    control={ctx.control}
                    render={({ field: { value, onChange } }) => (
                      <CKEditor4
                        id="org-payment-info-default"
                        value={value || ''}
                        onChange={onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}
